// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Wrench,
  Edit,
  Trash2,
  PlayCircle,
  CheckCircle,
  Calendar,
  Home,
  User,
  Building2,
  Tag,
  AlertTriangle,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import {
  useMaintenanceRequest,
  useUpdateMaintenanceRequest,
  useDeleteMaintenanceRequest,
} from '@/hooks/useMaintenanceRequests'
import { useDateFormatter } from '@/hooks/useDateFormatter'

export function MaintenanceDetailPage() {
  const { t, i18n } = useTranslation('maintenance')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const requestId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()

  const { data: request, isLoading, isFetching } = useMaintenanceRequest(requestId)
  const updateRequest = useUpdateMaintenanceRequest()
  const deleteRequest = useDeleteMaintenanceRequest()

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const getStatusBadge = () => {
    if (!request) return null
    switch (request.status) {
      case 'completed':
        return <Badge variant="success">{t('status.completed')}</Badge>
      case 'in_progress':
        return <Badge variant="info">{t('status.in_progress')}</Badge>
      case 'open':
      default:
        return <Badge variant="warning">{t('status.open')}</Badge>
    }
  }

  const getPriorityBadge = () => {
    if (!request) return null
    switch (request.priority) {
      case 'urgent':
        return <Badge variant="error">{t('priority.urgent')}</Badge>
      case 'normal':
        return <Badge variant="ghost">{t('priority.normal')}</Badge>
      case 'low':
      default:
        return <Badge variant="ghost">{t('priority.low')}</Badge>
    }
  }

  const handleMarkInProgress = async () => {
    await updateRequest.mutateAsync({ id: requestId, status: 'in_progress' })
  }

  const handleMarkCompleted = async () => {
    await updateRequest.mutateAsync({ id: requestId, status: 'completed' })
  }

  const handleDelete = async () => {
    await deleteRequest.mutateAsync(requestId)
    navigate('/maintenances')
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-48" />
          </Card>
        </div>
    )
  }

  if (!request) {
    return (
        <Card>
          <EmptyState
            title={t('empty.not_found')}
            action={<Button onClick={() => navigate('/maintenances')}>{t('actions.back_to_list')}</Button>}
          />
        </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title'), href: '/maintenances' }, { label: request.title }]} />

        <PageHeader
          title={request.title}
          description={t('detail.created_on', { date: formatDate(request.created_at) })}
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — primary is the one-step-forward status transition
          // (open → in_progress → completed); when open, a jump-straight-to-
          // completed shortcut still lives in the overflow (mirrors the old
          // two-button layout).
          primaryAction={
            request.status === 'open'
              ? {
                  key: 'mark-in-progress',
                  label: t('actions.mark_in_progress'),
                  icon: PlayCircle,
                  onClick: handleMarkInProgress,
                  loading: updateRequest.isPending,
                }
              : request.status === 'in_progress'
                ? {
                    key: 'mark-completed',
                    label: t('actions.mark_completed'),
                    icon: CheckCircle,
                    onClick: handleMarkCompleted,
                    loading: updateRequest.isPending,
                  }
                : undefined
          }
          overflowActions={[
            {
              key: 'mark-completed-shortcut',
              label: t('actions.mark_completed'),
              icon: CheckCircle,
              onClick: handleMarkCompleted,
              loading: updateRequest.isPending,
              hidden: request.status !== 'open',
            },
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/maintenances/${id}/edit`,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setShowDeleteModal(true),
              destructive: true,
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Info */}
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('detail.info_title')}</h2>
              {getStatusBadge()}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Home className="w-4 h-4" /> {t('labels.unit')}
                </dt>
                <dd className="font-medium break-words">{request.unit?.name || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <User className="w-4 h-4" /> {t('labels.customer')}
                </dt>
                <dd className="font-medium break-words">{request.customer?.name || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Building2 className="w-4 h-4" /> {t('labels.property')}
                </dt>
                <dd className="font-medium break-words">{request.property?.name || '-'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Tag className="w-4 h-4" /> {t('labels.category')}
                </dt>
                <dd className="font-medium break-words">
                  {/* request.category is operator-configurable (accountSettings.settings.maintenance_categories),
                      not a fixed backend enum — branch on i18n.exists() so custom categories render as-is. */}
                  {request.category
                    ? (i18n.exists(`maintenance:categories.${request.category}`)
                        ? t(`categories.${request.category}`)
                        : request.category)
                    : '-'}
                  {request.category === 'other' && request.category_note && (
                    <span className="text-base-content/60 font-normal ml-1">— {request.category_note}</span>
                  )}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> {t('labels.priority')}
                </dt>
                <dd>{getPriorityBadge()}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.created')}
                </dt>
                <dd className="font-medium">{formatDate(request.created_at)}</dd>
              </div>
              {request.completed_at && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> {t('labels.completed')}
                  </dt>
                  <dd className="font-medium">{formatDate(request.completed_at)}</dd>
                </div>
              )}
            </div>

            {/* Description */}
            {request.description && (
              <div className="border-t border-base-200 pt-4 mb-4">
                <h3 className="font-medium mb-2">{t('labels.description')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{request.description}</p>
              </div>
            )}

            {/* Notes */}
            {request.notes && (
              <div className="border-t border-base-200 pt-4">
                <h3 className="font-medium mb-2">{t('labels.notes')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{request.notes}</p>
              </div>
            )}
          </Card>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card
              className={`${
                request.status === 'completed'
                  ? 'bg-success/5 border-success'
                  : request.status === 'in_progress'
                    ? 'bg-info/5 border-info'
                    : 'bg-warning/5 border-warning'
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                {request.status === 'completed' ? (
                  <CheckCircle className="w-8 h-8 text-success" />
                ) : request.status === 'in_progress' ? (
                  <Wrench className="w-8 h-8 text-info" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-warning" />
                )}
                <div>
                  <div className="text-sm text-base-content/60">{tc('labels.status')}</div>
                  <div className="text-lg font-bold">
                    {t(`status.${request.status}`)}
                  </div>
                </div>
              </div>
              {request.status === 'open' && (
                <Button className="w-full" onClick={handleMarkInProgress} loading={updateRequest.isPending}>
                  {t('actions.mark_in_progress')}
                </Button>
              )}
              {request.status === 'in_progress' && (
                <Button className="w-full" onClick={handleMarkCompleted} loading={updateRequest.isPending}>
                  {t('actions.mark_completed')}
                </Button>
              )}
            </Card>

            {/* Priority Card */}
            <Card>
              <h2 className="text-lg font-semibold mb-4">{t('labels.priority')}</h2>
              <div className="flex items-center gap-2">
                {getPriorityBadge()}
              </div>
            </Card>
          </div>
        </div>

        {/* Photo Gallery */}
        {request.image_urls && request.image_urls.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.photos')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {request.image_urls.map((url, index) => (
                <a
                  key={index}
                  href={`${import.meta.env.VITE_API_URL || ''}/api/images/${url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square rounded-lg overflow-hidden border border-base-200 hover:border-primary transition-colors"
                >
                  <img
                    src={`${import.meta.env.VITE_API_URL || ''}/api/images/${url}`}
                    alt={`${t('labels.photo')} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('messages.delete_title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteRequest.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_confirm', { title: request.title })}</p>
      </Modal>
    </>
  )
}
